'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Sparkles, Target, Shield, Zap, Flame, CheckCircle2 } from 'lucide-react'
import { StarIcon } from './medallions/star-icon'
import type { WeeklyQuestsResult } from '@/lib/phishtrainer/types'

const ICON_MAP = {
  sparkles: Sparkles,
  target: Target,
  shield: Shield,
  zap: Zap,
  flame: Flame,
} as const

export interface WeeklyQuestsCardProps {
  data: WeeklyQuestsResult
}

export function WeeklyQuestsCard({ data }: WeeklyQuestsCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" /> Weekly quests
          <Badge variant="outline" className="ml-1 text-[10px]">
            {data.totalStarsEarned}/{data.totalStarsAvailable} <StarIcon size={10} /> this week
          </Badge>
        </CardTitle>
        <span className="text-xs text-muted-foreground">Rotates every Monday</span>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {data.quests.map((q) => {
            const Icon = ICON_MAP[q.iconKey] ?? Zap
            const pct = Math.min(100, Math.round((q.progress / q.target) * 100))
            return (
              <div
                key={q.id}
                className={
                  'relative overflow-hidden rounded-xl border p-3 transition-colors ' +
                  (q.completed
                    ? 'border-emerald-500/40 bg-emerald-500/5'
                    : 'border-border bg-muted/30')
                }
              >
                <div
                  className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-25"
                  style={{ background: `radial-gradient(circle, ${q.color}, transparent)` }}
                />
                <div className="relative flex items-start gap-3">
                  <div
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-white shadow"
                    style={{ background: `linear-gradient(135deg, ${q.color}, ${q.color}aa)` }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-semibold text-foreground truncate">{q.name}</div>
                      {q.completed && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                      {q.description}
                    </div>
                    <div className="mt-2">
                      <Progress value={pct} className="h-1" />
                      <div className="mt-1 flex items-center justify-between text-[10px]">
                        <span className="tabular-nums text-muted-foreground">
                          {q.progress}/{q.target}
                        </span>
                        <span className="inline-flex items-center gap-0.5 font-semibold text-amber-600 dark:text-amber-400">
                          +{q.starsReward} <StarIcon size={10} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
